import formatter from '../../../common/helpers/formatter';
import type { Form } from '../../../common/types/form';
import type { Client } from '../../../common/types/client';
import type { Setting } from '../../../common/types/settings';

const coreDataForm = (advisors: Setting[]): Form<Client> => {
  const advisorOptions = advisors.map((advisor) => advisor.value);

  return {
    key: 'coreData',
    title: 'Stammdaten',
    fields: [
      {
        key: 'name',
        label: 'Name',
        fieldTypes: 'text',
      },
      {
        key: 'phone',
        label: 'Telefon',
        fieldTypes: 'text',
      },
      {
        key: 'email',
        label: 'E-Mail',
        fieldTypes: 'text',
      },
      {
        key: 'address',
        label: 'Anschrift',
        fieldTypes: 'text',
      },
      {
        key: 'advisor',
        label: 'Beraterin',
        fieldTypes: 'autocomplete',
        options: advisorOptions,
      },
      {
        key: 'gender',
        label: 'Geschlecht',
        fieldTypes: 'select',
        options: [
          {
            value: 'female',
            label: 'Weiblich',
          },
          {
            value: 'male',
            label: 'Männlich',
          },
          {
            value: 'trans',
            label: 'Transgender',
          },
          {
            value: 'nonBinary',
            label: 'Inter/nichtbinär',
          },
        ],
      },
      {
        key: 'birthday',
        label: 'Geburtstag',
        fieldTypes: 'date',
        helpText: (client) => {
          const age = client.birthday != null ? formatter.getYearRange(client.birthday) : null;
          if (age == null) {
            return null;
          }
          const label = age === 1 ? 'Jahr' : 'Jahre';
          return `${age} ${label}`;
        },
      },
      {
        key: 'location',
        label: 'Ort',
        fieldTypes: 'select',
        options: [
          {
            value: 'nuremberg',
            label: 'Nürnberg',
          },
          {
            value: 'nurembergRegion',
            label: 'Nürnberger Land',
          },
          {
            value: 'fuerth',
            label: 'Stadt Fürth',
          },
          {
            value: 'fuerthRegion',
            label: 'Lkr. Fürth',
          },
          {
            value: 'ansbach',
            label: 'Stadt Ansbach',
          },
          {
            value: 'ansbachRegion',
            label: 'Lkr. Ansbach',
          },
          {
            value: 'erlangen',
            label: 'Stadt Erlangen',
          },
          {
            value: 'erlangenRegion',
            label: 'Lkr. Erlangen-Höchstadt',
          },
          {
            value: 'schwabach',
            label: 'Stadt Schwabach',
          },
          {
            value: 'upperPalatinate',
            label: 'Oberpfalz',
          },
          {
            value: 'neustadtAischRegion',
            label: 'Lkr. Neustadt a.d. Aisch',
          },
          {
            value: 'rothRegion',
            label: 'Lkr. Roth',
          },
          {
            value: 'weissenburgGunzenhausenRegion',
            label: 'Lkr. Weißenburg-Gunzenhausen ',
          },
          {
            value: 'locationOther',
            label: 'Sonstiges',
          },
        ],
      },
      {
        key: 'locationOther',
        label: 'Sonstiger Ort',
        fieldTypes: 'text',
        isVisible: (client) => client.location != null && client.location === 'locationOther',
      },
      {
        key: 'personType',
        label: 'Person',
        fieldTypes: 'select',
        options: [
          {
            value: 'personAffected',
            label: 'Betroffene',
          },
          {
            value: 'supportivePerson',
            label: 'Unterstützende Bezugsperson',
          },
          {
            value: 'professional',
            label: 'Fachkraft',
          },
          {
            value: 'child',
            label: 'Kind',
          },
        ],
      },
      {
        key: 'supportivePersonRelationship',
        label: 'Beziehung zur Betroffenen',
        fieldTypes: 'text',
        isVisible: (client) => client.personType != null && client.personType === 'supportivePerson',
      },
      {
        key: 'professionalMission',
        label: 'Auftrag in Bezug auf Betroffene',
        fieldTypes: 'text',
        isVisible: (client) => client.personType != null && client.personType === 'professional',
      },
      {
        key: 'affectedPersonsInCounselling',
        fieldTypes: 'checkboxSelect',
        isVisible: (client) =>
          client.personType != null &&
          (client.personType === 'professional' || client.personType === 'supportivePerson'),
        options: [
          {
            value: 'affectedPersonsInCounselling',
            label: 'Betroffene auch bei uns in Beratung',
          },
        ],
      },
      {
        key: 'affectedPersonsInCounsellingBy',
        label: 'Bei',
        fieldTypes: 'text',
        isVisible: (client) =>
          client.personType != null &&
          (client.personType === 'professional' || client.personType === 'supportivePerson') &&
          client.affectedPersonsInCounselling != null &&
          client.affectedPersonsInCounselling.includes('affectedPersonsInCounselling'),
      },
      {
        key: 'childMother',
        label: 'Name der Mutter',
        fieldTypes: 'text',
        isVisible: (client) => client.personType != null && client.personType === 'child',
      },
      {
        key: 'notice',
        label: 'Bemerkung',
        xsWidth: 12,
        fieldTypes: 'textArea',
      },
    ],
  };
};

export default coreDataForm;
